import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';

import { ApiClientService } from '../../../core/http/api-client.service';
import {
  CreateSpecialistDowntimeRequest,
  DeleteSpecialistDowntimeResponse,
  SpecialistDowntimeFilters,
  SpecialistDowntimeCreateContext,
  SpecialistDowntimeImpactPreview,
  SpecialistDowntimeItem,
  SpecialistDowntimeItemDto,
  SpecialistDowntimeListSort,
  SpecialistDowntimePage,
  SpecialistDowntimePageDto,
  SpecialistDowntimePendingSummary,
  UpdateSpecialistDowntimeRequest
} from './specialist-downtime.models';
import { mapSpecialistDowntimeItem } from './specialist-downtime.mapper';

@Injectable({
  providedIn: 'root'
})
export class SpecialistDowntimeService
{
  private readonly apiClient = inject(ApiClientService);

  public getList(
    filters?: SpecialistDowntimeFilters
  ): Observable<SpecialistDowntimeItem[]>
  {
    const query = filters
      ? {
          clinicId: filters.clinicId,
          specialistUserId: filters.specialistUserId,
          status: filters.status,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo
        }
      : undefined;

    return this.apiClient.get<SpecialistDowntimeItemDto[] | null>(
      '/specialist-downtime',
      query
    ).pipe(
      map((items: SpecialistDowntimeItemDto[] | null) =>
      {
        if (!Array.isArray(items))
        {
          return [];
        }

        return items.map(mapSpecialistDowntimeItem);
      })
    );
  }

  public getPage(
    params: {
      limit?: number;
      cursor?: string | null;
      sort?: SpecialistDowntimeListSort;
      pendingFirst?: boolean;
      filters?: SpecialistDowntimeFilters;
    } = {},
  ): Observable<SpecialistDowntimePage>
  {
    return this.apiClient.get<SpecialistDowntimePageDto>(
      '/specialist-downtime',
      {
        limit: params.limit,
        cursor: params.cursor || undefined,
        pendingFirst: params.pendingFirst,
        sort: params.sort,
        clinicId: params.filters?.clinicId,
        specialistUserId: params.filters?.specialistUserId,
        status: params.filters?.status,
        dateFrom: params.filters?.dateFrom,
        dateTo: params.filters?.dateTo,
      }
    ).pipe(
      map((page: SpecialistDowntimePageDto) => ({
        items: Array.isArray(page.items)
          ? page.items.map(mapSpecialistDowntimeItem)
          : [],
        nextCursor: page.nextCursor ?? null,
        hasMore: !!page.hasMore,
      }))
    );
  }

  public getPendingSummary(): Observable<SpecialistDowntimePendingSummary>
  {
    return this.apiClient.get<SpecialistDowntimePendingSummary>(
      '/specialist-downtime/pending-summary'
    );
  }

  public getCreateContext(): Observable<SpecialistDowntimeCreateContext>
  {
    return this.apiClient.get<SpecialistDowntimeCreateContext>(
      '/specialist-downtime/create-context'
    ).pipe(
      map((context: SpecialistDowntimeCreateContext) => ({
        clinics: Array.isArray(context.clinics) ? context.clinics : [],
        specialists: Array.isArray(context.specialists) ? context.specialists : [],
      }))
    );
  }

  public getById(
    id: string
  ): Observable<SpecialistDowntimeItem>
  {
    return this.apiClient.get<SpecialistDowntimeItemDto>(
      `/specialist-downtime/${id}`
    ).pipe(
      map(mapSpecialistDowntimeItem)
    );
  }

  public create(
    payload: CreateSpecialistDowntimeRequest
  ): Observable<SpecialistDowntimeItem>
  {
    return this.apiClient.post<SpecialistDowntimeItemDto, CreateSpecialistDowntimeRequest>(
      '/specialist-downtime',
      payload
    ).pipe(
      map(mapSpecialistDowntimeItem)
    );
  }

  public previewImpact(
    payload: CreateSpecialistDowntimeRequest
  ): Observable<SpecialistDowntimeImpactPreview>
  {
    return this.apiClient.post<SpecialistDowntimeImpactPreview, CreateSpecialistDowntimeRequest>(
      '/specialist-downtime/impact-preview',
      payload
    ).pipe(
      map((preview: SpecialistDowntimeImpactPreview) => ({
        affectedAppointmentIds: Array.isArray(preview.affectedAppointmentIds)
          ? preview.affectedAppointmentIds
          : [],
        reachabilityImpact: preview.reachabilityImpact ?? null,
      }))
    );
  }

  public update(
    id: string,
    payload: UpdateSpecialistDowntimeRequest
  ): Observable<SpecialistDowntimeItem>
  {
    return this.apiClient.patch<SpecialistDowntimeItemDto, UpdateSpecialistDowntimeRequest>(
      `/specialist-downtime/${id}`,
      payload
    ).pipe(
      map(mapSpecialistDowntimeItem)
    );
  }

  public delete(
    id: string
  ): Observable<DeleteSpecialistDowntimeResponse>
  {
    return this.apiClient.delete<DeleteSpecialistDowntimeResponse>(
      `/specialist-downtime/${id}`
    );
  }

  public approve(
    id: string
  ): Observable<SpecialistDowntimeItem>
  {
    return this.apiClient.patch<SpecialistDowntimeItemDto, Record<string, never>>(
      `/specialist-downtime/${id}/approve`,
      {}
    ).pipe(
      map(mapSpecialistDowntimeItem)
    );
  }

  public reject(
    id: string
  ): Observable<SpecialistDowntimeItem>
  {
    return this.apiClient.patch<SpecialistDowntimeItemDto, Record<string, never>>(
      `/specialist-downtime/${id}/reject`,
      {}
    ).pipe(
      map(mapSpecialistDowntimeItem)
    );
  }

  public resolve(
    id: string
  ): Observable<SpecialistDowntimeItem>
  {
    return this.apiClient.patch<SpecialistDowntimeItemDto, Record<string, never>>(
      `/specialist-downtime/${id}/resolve`,
      {}
    ).pipe(
      map(mapSpecialistDowntimeItem)
    );
  }
}
